"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
exports.__esModule = true;
exports.Player = void 0;
var React = __importStar(require("react"));
var classnames_1 = __importDefault(require("classnames"));
var lodash_1 = require("lodash");
var react_1 = require("@xstate/react");
var video_context_1 = require("../context/video-context");
var use_metadata_cues_1 = require("../hooks/use-metadata-cues");
var use_auto_hide_controls_1 = require("../hooks/use-auto-hide-controls");
var browser = __importStar(require("../utils/browser"));
var video_1 = require("./video");
var shortcut_1 = require("./shortcut");
var loading_spinner_1 = require("./loading-spinner");
var big_play_button_1 = require("./big-play-button");
var bezel_1 = require("./bezel");
var control_bar_1 = require("./control-bar");
var progress_bar_1 = require("./progress-bar");
var cue_bar_1 = require("./cue-bar");
var cue_form_1 = require("./cue-form");
var selectors_1 = require("../selectors");
var usePlayerState = function () {
    var videoService = (0, video_context_1.useVideo)();
    var hasStarted = (0, react_1.useSelector)(videoService, selectors_1.selectHasStarted);
    var isActive = (0, react_1.useSelector)(videoService, selectors_1.selectIsActive);
    var paused = (0, react_1.useSelector)(videoService, selectors_1.selectIsPaused);
    var isSeeking = (0, react_1.useSelector)(videoService, selectors_1.selectIsSeeking);
    var isFullscreen = (0, react_1.useSelector)(videoService, selectors_1.selectIsFullscreen);
    var withSidePanel = (0, react_1.useSelector)(videoService, selectors_1.selectWithSidePanel);
    var isWaiting = (0, react_1.useSelector)(videoService, selectors_1.selectIsWaiting);
    var video = (0, react_1.useSelector)(videoService, selectors_1.selectVideo);
    var viewer = (0, react_1.useSelector)(videoService, selectors_1.selectViewer);
    return {
        videoService: videoService,
        isActive: isActive,
        hasStarted: hasStarted,
        isSeeking: isSeeking,
        paused: paused,
        isFullscreen: isFullscreen,
        withSidePanel: withSidePanel,
        isWaiting: isWaiting,
        video: video,
        viewer: viewer
    };
};
/**
 * The primary player instance. Must be a descendent by a {VideoProvider}.
 * @param props {PlayerProps}
 * @constructor
 */
var Player = function (props) {
    var children = props.children, controls = props.controls, className = props.className, _a = props.container, container = _a === void 0 ? null : _a, _b = props.fluid, fluid = _b === void 0 ? true : _b, overlay = props.overlay, _c = props.canAddNotes, canAddNotes = _c === void 0 ? false : _c, poster = props.poster, _d = props.enableGlobalShortcuts, enableGlobalShortcuts = _d === void 0 ? true : _d;
    var containerRef = React.useRef(container);
    var _e = usePlayerState(), videoService = _e.videoService, isActive = _e.isActive, hasStarted = _e.hasStarted, isSeeking = _e.isSeeking, paused = _e.paused, isFullscreen = _e.isFullscreen, withSidePanel = _e.withSidePanel, isWaiting = _e.isWaiting, video = _e.video, viewer = _e.viewer;
    var cues = (0, use_metadata_cues_1.useMetadataCues)();
    var _f = (0, use_auto_hide_controls_1.useAutoHideControls)(), controlsHidden = _f.controlsHidden, setControlsHidden = _f.setControlsHidden, setControlsHovered = _f.setControlsHovered;
    var handleActivity = function () {
        videoService.send('ACTIVITY');
        isFullscreen && setControlsHidden(false);
    };
    function setWidthOrHeight(style, name, value) {
        var _a;
        var styleVal;
        if (typeof value === 'string') {
            if (value === 'auto') {
                styleVal = 'auto';
            }
            else if (value.match(/\d+%/)) {
                styleVal = value;
            }
        }
        else if (typeof value === 'number') {
            styleVal = "".concat(value, "px");
        }
        Object.assign(style, (_a = {},
            _a[name] = styleVal,
            _a));
    }
    function getAspectRatioStyle() {
        var _a;
        var propsAspectRatio = props.aspectRatio, propsHeight = props.height, propsWidth = props.width;
        var style = {};
        var width;
        var height;
        var aspectRatio;
        // The aspect ratio is either used directly or to calculate width and height.
        if (propsAspectRatio !== undefined && propsAspectRatio !== 'auto') {
            // Use any aspectRatio that's been specifically set
            aspectRatio = propsAspectRatio;
        }
        else if (video === null || video === void 0 ? void 0 : video.videoWidth) {
            // Otherwise try to get the aspect ratio from the video metadata
            aspectRatio = "".concat(video.videoWidth, ":").concat(video.videoHeight);
        }
        else {
            // Or use a default. The video element's is 2:1, but 16:9 is more common.
            aspectRatio = '16:9';
        }
        // Get the ratio as a decimal we can use to calculate dimensions
        var ratioParts = aspectRatio.split(':');
        var ratioMultiplier = Number(ratioParts[1]) / Number(ratioParts[0]);
        if (propsWidth !== undefined) {
            // Use any width that's been specifically set
            width = propsWidth;
        }
        else if (propsHeight !== undefined) {
            // Or calulate the width from the aspect ratio if a height has been set
            width = Number(propsHeight) / ratioMultiplier;
        }
        else {
            // Or use the video's metadata, or use the video el's default of 300
            width = (_a = video === null || video === void 0 ? void 0 : video.videoWidth) !== null && _a !== void 0 ? _a : 400;
        }
        if (propsHeight !== undefined) {
            // Use any height that's been specifically set
            height = propsHeight;
        }
        else {
            // Otherwise calculate the height from the ratio and the width
            height = Number(width) * ratioMultiplier;
        }
        if (fluid) {
            style.paddingTop = "".concat(ratioMultiplier * 100, "%");
        }
        else {
            // If Width contains "auto", set "auto" in style
            setWidthOrHeight(style, 'width', width);
            setWidthOrHeight(style, 'height', height);
        }
        return style;
    }
    return (React.createElement("div", { ref: function (c) {
            containerRef.current = container ? container : c;
            // creating custom object to avoid circular ref error
            var domNodeRef = __assign(__assign({}, containerRef), { toJSON: function () {
                    return {};
                } });
            videoService.send({
                type: 'SET_ROOT_ELEM',
                rootElemRef: domNodeRef
            });
        }, onMouseDown: handleActivity, onMouseMove: handleActivity, onKeyDown: handleActivity, onPointerMove: function () {
            setControlsHidden(false);
        }, className: (0, classnames_1["default"])({
            'cueplayer-react-controls-enabled': true,
            'cueplayer-react-has-started': hasStarted,
            'cueplayer-react-paused': paused,
            'cueplayer-react-playing': !paused,
            'cueplayer-react-waiting': isWaiting,
            'cueplayer-react-seeking': isSeeking,
            'cueplayer-react-fluid': fluid,
            'cueplayer-react-fullscreen': isFullscreen,
            'cueplayer-react-with-side-panel': withSidePanel,
            'cueplayer-react-user-inactive': !isActive,
            'cueplayer-react-user-active': isActive,
            'cueplayer-react-workinghover': !browser.IS_IOS,
            'cueplayer-react-cues-active': !(0, lodash_1.isEmpty)(cues)
        }, 'cueplayer-react', className) },
        React.createElement("div", { className: (0, classnames_1["default"])('fullscreen-wrapper', {
                fullscreen: isFullscreen
            }) },
            React.createElement("div", { className: "cueplayer-react-video-wrapper" },
                React.createElement("div", { style: getAspectRatioStyle(), className: "cueplayer-react-video-holder" },
                    React.createElement(video_1.Video, { poster: poster }, children),
                    React.createElement(big_play_button_1.BigPlayButton, null),
                    React.createElement(bezel_1.Bezel, null),
                    React.createElement(loading_spinner_1.LoadingSpinner, null)))),
        React.createElement("div", { className: (0, classnames_1["default"])('cueplayer-react-controls-holder', {
                'cueplayer-react-controls-holder-auto-hide': controlsHidden
            }), onMouseOver: function () {
                setControlsHovered(true);
            }, onMouseOut: function () {
                setControlsHovered(false);
            } },
            React.createElement(progress_bar_1.ProgressBar, null),
            React.createElement(cue_bar_1.CueBar, null),
            React.createElement(control_bar_1.ControlBar, null, controls),
            React.createElement(shortcut_1.Shortcut, { enableGlobalShortcuts: enableGlobalShortcuts, canAddNotes: canAddNotes }),
            canAddNotes && React.createElement(cue_form_1.CueForm, null)),
        overlay));
};
exports.Player = Player;
